// Decode JWT token
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { getRefreshToken, hasForeignAudience, isTokenExpired, useAuthContext } from "@/contexts";
import { DASHBOARD_APP_ID } from "@/utils/auth";

import { PortalRedirect } from "./PortalRedirect";

export type AuthGuardProps = {
  children: ReactNode;
};

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const { accessToken, isInitialized } = useAuthContext();
  const location = useLocation();

  // A session minted for another app is being ended by the provider; rendering
  // the app meanwhile would only fire requests that 403.
  if (!isInitialized || hasForeignAudience(accessToken, DASHBOARD_APP_ID)) {
    return null;
  }

  // An expired access token is only terminal without a refresh token: with one,
  // the provider bootstraps a refresh (holding `isInitialized` until it settles)
  // and clears every auth cell if that refresh is rejected — which lands here as
  // a missing token and bounces to the portal.
  if (!accessToken || (isTokenExpired(accessToken) && !getRefreshToken())) {
    // Go straight to the Tournated-ID portal (no /login hop). The portal returns
    // the user to wherever they were headed.
    return <PortalRedirect from={location.pathname + location.search} />;
  }

  return <>{children}</>;
};
